import { EnhancedStatementRow } from '../../financialStatements/utils/dataEnhancement'
import { StatementColumn, StatementDataType } from '../../financialStatements/types'
import { AllocationBudgetRow, MonthlyBudget } from '../types'
import { AccountMonthlyBudget } from '../../account/types/AccountMonthlyBudget'
import { DEFAULT_ALLOCATION_METHOD } from '../constants'
import { convertMonthlyBudgets } from './conversionUtils'
import { VariableType } from '../../../components/Table/types'

/**
 * Converts EnhancedStatementRow[] to AllocationBudgetRow[]
 *
 * Flattens newBudgets.groups entries - each group entry becomes a separate row.
 * This allows user to configure allocation method for each aggregated cell individually.
 *
 * @param rowDatas - Grid rows with newBudgets.groups changes
 * @param columns - Grid columns (needed to map dataKey → periodGroup)
 * @returns Array of allocation rows for modal
 *
 * @example
 * ```typescript
 * // If row has newBudgets.groups = { [fyId]: 120000, [q1Id]: 30000 }
 * // Result will have 2 AllocationBudgetRows (one per group entry)
 * const allocationRows = convertToAllocationRows(rowData, columns)
 * ```
 */
export const convertAccountRowToAllocationRows = (
  rowDatas: EnhancedStatementRow[],
  columns: StatementColumn[]
): AllocationBudgetRow[] => {
  const allocationRows: AllocationBudgetRow[] = []

  // Build dataKey → column lookup for groups columns only
  const groupColumnsMap = new Map<number, StatementColumn>()
  columns
    .filter(col => col.dataSource === 'groups' && col.dataKey != null)
    .forEach(col => {
      groupColumnsMap.set(col.dataKey!, col)
    })

  // Process each row
  rowDatas.forEach(rowData => {
    const { _sourceAccountCode, newBudgets, actuals, dimensionId, dimensionName, name, budgetingMethod } = rowData

    // Skip rows without account code or groups changes
    if (!_sourceAccountCode || !newBudgets?.groups) {
      return
    }

    // Determine variable type based on budgetingMethod
    const variableType: VariableType =
      budgetingMethod?.method === 'reference' ? VariableType.percentage : VariableType.absolute

    // Process each groups entry
    Object.entries(newBudgets.groups).forEach(([dataKeyStr, value]) => {
      const dataKey = parseInt(dataKeyStr, 10)
      const column = groupColumnsMap.get(dataKey)

      if (!column || !column.periodGroup) {
        console.warn(`No column found for dataKey ${dataKey}`)
        return
      }

      const { periodGroup } = column
      const targetPeriods = periodGroup.periods || []

      // Create unique ID: accountCode_dimensionId_dataKey
      const id = `${_sourceAccountCode}_${dimensionId || 'null'}_${dataKey}`

      // If value is null, mark as deletion (remove all budgets for this period)
      const isDeletion = value == null

      const row: AllocationBudgetRow = {
        id,
        entityType: 'account',
        accountCode: _sourceAccountCode,
        accountName: name,
        dimensionId: dimensionId || null,
        dimensionName,
        actuals,
        dataType: 'budget',
        variableType,
        style: {
          decimals: variableType === VariableType.percentage ? 2 : 0
        },
        dataKey,
        periodGroup,
        targetPeriods,
        periodLabel: column.label,
        periodLength: targetPeriods.length,
        value: isDeletion ? 0 : value, // Use 0 as placeholder for deleted budgets
        isDeletion,
        allocationMethod: DEFAULT_ALLOCATION_METHOD,
        allocationOptions: undefined
      }

      allocationRows.push(row)
    })
  })

  return allocationRows
}

/**
 * Converts MonthlyBudget array to AccountMonthlyBudget array by adding account context.
 * This is the per-row converter used by applyAllocation.
 *
 * @param monthlyBudgets - Array of monthly budget allocations (output from allocation functions)
 * @param row - Allocation row containing accountCode, dimensionId, and dataType
 * @returns Array of AccountMonthlyBudget ready for backend API
 */
export const convertMonthlyBudgetsToAccountData = (
  monthlyBudgets: MonthlyBudget[],
  row: AllocationBudgetRow
): AccountMonthlyBudget[] => {
  return convertMonthlyBudgets<AccountMonthlyBudget>(
    monthlyBudgets,
    row.accountCode,
    row.dimensionId,
    row.dataType as StatementDataType,
    'accountCode'
  )
}
